// filepath: /src/hooks/useSmoothScroll.js
import { useEffect, useCallback } from 'react';

const useSmoothScroll = () => {
  const scrollToElement = useCallback((elementId, offset = 20) => {
    const element = document.getElementById(elementId);
    
    if (!element) {
      console.warn(`Element with id "${elementId}" not found`);
      return;
    }

    // Get navbar height for proper offset
    const navbar = document.querySelector('.frosted-navbar');
    const navbarHeight = navbar ? navbar.offsetHeight : 80;
    const totalOffset = navbarHeight + (offset || 20);
    
    // Check if browser supports smooth scrolling
    const supportsNativeSmooth = 'scrollBehavior' in document.documentElement.style;
    
    if (supportsNativeSmooth) {
      // Use modern scroll behavior with proper block positioning
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const targetPosition = absoluteElementTop - totalOffset;
      
      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: 'smooth'
      });
    } else {
      // Fallback for older browsers
      const elementPosition = element.offsetTop - totalOffset;
      
      // Manual smooth scroll implementation
      const startPosition = window.pageYOffset;
      const targetPosition = Math.max(0, elementPosition);
      const distance = targetPosition - startPosition;
      const duration = 800; // 800ms animation
      let start = null;

      function animateScroll(timestamp) {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const percentage = Math.min(progress / duration, 1);
        
        // Easing function (ease-out cubic)
        const ease = 1 - Math.pow(1 - percentage, 3);
        
        window.scrollTo(0, startPosition + distance * ease);
        
        if (progress < duration) {
          requestAnimationFrame(animateScroll);
        }
      }
      
      requestAnimationFrame(animateScroll);
    }
  }, []);

  const handleAnchorClick = useCallback((href) => {
    if (typeof href === 'string' && href.startsWith('#')) {
      const elementId = href.replace('#', '');
      scrollToElement(elementId);
    }
  }, [scrollToElement]);

  useEffect(() => {
    // Global smooth scroll handler for all anchor links
    const handleDocumentClick = (e) => {
      // Check if the clicked element is an anchor or has an anchor parent
      let target = e.target;
      while (target && target.tagName !== 'A') {
        target = target.parentElement;
      }
      
      if (target && target.tagName === 'A') {
        const href = target.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          handleAnchorClick(href);
        }
      }
    };

    // Add click listener to document for all anchor links
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleAnchorClick]);

  return { scrollToElement, handleAnchorClick };
};

export default useSmoothScroll;
